// src/components/toolkit/StoryStyleFinder.jsx — goes with Module 1.
//
// The module teaches six family story styles and the Real-Talk formula. This
// shows the styles by what they sound like rather than by name, so you pick
// the one your family actually resembles instead of the one that flatters it.
import { useState } from 'react';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import { FAMILY_STORY_STYLES, REAL_TALK_FORMULA } from '../../data/toolkitData';
import { loadTool, saveTool } from './toolStorage';

const TOOL_ID = 'story-style-finder';
const card = 'rounded-xl border border-thread-border bg-white/5 p-5';

const StoryStyleFinder = ({ onBack, onComplete }) => {
  const [state, setState] = useState(() => loadTool(TOOL_ID, { picked: [], done: false }));
  const picked = state.picked || [];

  const update = (next) => {
    const merged = { ...state, ...next };
    setState(merged);
    saveTool(TOOL_ID, merged);
  };

  const toggle = (name) => {
    if (picked.includes(name)) {
      update({ picked: picked.filter((n) => n !== name) });
    } else if (picked.length < 2) {
      update({ picked: [...picked, name] });
    }
  };

  const chosen = FAMILY_STORY_STYLES.filter((style) => picked.includes(style.name));

  const finish = () => {
    update({ done: true });
    onComplete?.({ styles: picked });
  };

  return (
    <div className="min-h-screen bg-thread-darker text-white pt-36 pb-20">
      <div className="container mx-auto px-4 max-w-3xl">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 min-h-[44px] text-thread-sand hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to the toolkit
        </button>

        <h1 className="text-3xl md:text-4xl font-bold font-display mb-3">Family story style finder</h1>
        <p className="text-slate-300 mb-8">
          Every family has a habit when it retells a hard year. Pick the one or two that sound most like yours. Nobody
          gets a good one and a bad one: each has something it does well and something it skips.
        </p>

        <ul className="space-y-3 mb-8">
          {FAMILY_STORY_STYLES.map((style) => {
            const active = picked.includes(style.name);
            const full = picked.length >= 2 && !active;
            return (
              <li key={style.name}>
                <button
                  type="button"
                  onClick={() => toggle(style.name)}
                  aria-pressed={active}
                  disabled={full}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${
                    active
                      ? 'border-thread-glow bg-thread-ember/15'
                      : full
                        ? 'border-white/10 bg-white/5 opacity-50'
                        : 'border-white/15 bg-white/5 hover:border-thread-glow/60'
                  }`}
                >
                  <span className="flex items-start gap-3">
                    <span
                      className={`mt-1 w-5 h-5 shrink-0 rounded border flex items-center justify-center ${
                        active ? 'border-thread-glow bg-thread-ember text-white' : 'border-white/30'
                      }`}
                      aria-hidden="true"
                    >
                      {active && <Check className="w-3.5 h-3.5" />}
                    </span>
                    <span className="text-slate-200 italic">&ldquo;{style.soundsLike}&rdquo;</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {chosen.length > 0 && (
          <section aria-label="What that style is like" className="space-y-4 mb-8">
            {chosen.map((style) => (
              <div key={style.name} className={card}>
                <h2 className="text-xl font-bold text-white">{style.name}</h2>
                <p className="text-thread-sand mb-4">{style.motto}</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <h3 className="font-semibold text-white mb-1">Good at</h3>
                    <ul className="list-disc list-inside text-slate-300 space-y-1">
                      {style.strengths.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Tends to skip</h3>
                    <ul className="list-disc list-inside text-slate-300 space-y-1">
                      {style.blindSpots.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}

            <div className={card}>
              <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-1">
                <Sparkles className="w-5 h-5 text-thread-gold" aria-hidden="true" /> What to aim for
              </h2>
              <p className="text-slate-300 mb-3">
                Module 1 calls this the Real-Talk formula. Take one family story you already tell and put all three in it.
              </p>
              <ol className="space-y-2">
                {REAL_TALK_FORMULA.map((part, index) => (
                  <li key={part.name} className="text-slate-300">
                    <span className="font-semibold text-white">
                      {index + 1}. {part.name}:
                    </span>{' '}
                    {part.description}
                  </li>
                ))}
              </ol>
            </div>

            <button
              type="button"
              onClick={finish}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-thread-ember hover:bg-thread-rust text-white font-semibold transition-colors"
            >
              {state.done ? 'Saved' : 'Save this'} <Check className="w-4 h-4" aria-hidden="true" />
            </button>
            {state.done && <p className="text-sm text-slate-400">Saved on this device.</p>}
          </section>
        )}
      </div>
    </div>
  );
};

export default StoryStyleFinder;
